import { useMutation } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { toast } from "react-hot-toast";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { links } from "@/data/links";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";
import { createHttpErrorMessage } from "@/utils/create-http-error-message";

import { PlanType } from "../types/stripe-plan";

type VisitStripePaymentProps = {
  token: string;
  planName: Exclude<PlanType, "Free">;
  successUrl: string;
  cancelUrl: string;
  discount: string | null;
  lang: Lang;
};

const visitStripePaymentSchema = z.object({
  data: z.object({
    sessionUrl: z.string(),
  }),
  message: z.string(),
});

export const visitStripePayment = async ({
  token,
  planName,
  successUrl,
  cancelUrl,
  discount,
  lang,
}: VisitStripePaymentProps): Promise<string> => {
  if (!token || !planName || !successUrl || !cancelUrl)
    throw new Error("missing params");
  const endpoint = "/subscribe/plan";
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-User-Profile": token,
      "Accept-Language": lang,
    },
    body: JSON.stringify({
      item: planName,
      successUrl,
      cancelUrl,
      discount,
    }),
  });
  if (!res.ok) {
    const errorMessage = await createHttpErrorMessage(endpoint, res);
    throw new Error(errorMessage);
  }
  return visitStripePaymentSchema.parse(await res.json()).data.sessionUrl;
};

export const useVisitStripePayment = () => {
  const router = useRouter();
  const { t, lang } = useLang();
  const { backendToken: token, profile } = useAuth();
  const pathname = usePathname();
  const discount = handleDiscount(profile?.discount);
  const searchParams = useSearchParams();
  const duration = searchParams.get("duration");

  return useMutation(
    ({ planName }: Pick<VisitStripePaymentProps, "planName">) =>
      visitStripePayment({
        token: token ?? "",
        planName,
        successUrl: `${window.location.origin}/${lang}${links.paymentSuccess}`,
        cancelUrl: `${window.location.origin}${pathname}${
          duration ? `?duration=${duration}` : ""
        }`,
        discount,
        lang,
      }),
    {
      onSuccess: (paymentUrl) => {
        if (paymentUrl) router.push(paymentUrl);
      },
      onError: () => {
        toast.error(t.error.serverError);
      },
    }
  );
};

const handleDiscount = (discount: string[] | undefined = []) =>
  discount?.length > 0 ? discount?.[0] : null;
